import { useEffect, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import { portfolioProjects } from '@/data/siteData';
import { scrollReveal, staggerFadeIn, prefersReducedMotion } from '@/hooks/useGsap';
import { Button } from '@/components/ui/button';

const Portfolio = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sectionRef.current) {
      const titleEl = sectionRef.current.querySelector('.section-title');
      if (titleEl) scrollReveal(titleEl as HTMLElement);
    }
    if (cardsRef.current) {
      const cards = cardsRef.current.querySelectorAll('.portfolio-card');
      if (!prefersReducedMotion()) {
        staggerFadeIn(cards, { delay: 0.2, stagger: 0.15 });
      }
    }
  }, []);

  return (
    <section id="portfolio" ref={sectionRef} className="section-padding section-atmosphere bg-muted/10 border-t border-border">
      <div className="container-site">
        <div className="section-title text-center mb-12" style={{ opacity: prefersReducedMotion() ? 1 : 0 }}>
          <span className="chip mb-4">Portfolio</span>
          <h2 className="font-display uppercase font-light text-3xl sm:text-4xl tracking-[0.03em] text-foreground">
            Recent Projects
          </h2>
          <p className="text-muted-foreground text-xs leading-relaxed mt-3 max-w-xl mx-auto uppercase tracking-wide">
            Beberapa project yang udah live — dari edukasi sampai e-commerce.
          </p>
        </div>

        <div ref={cardsRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {portfolioProjects.map((project) => (
            <article
              key={project.id}
              className="portfolio-card motion-panel group bg-card overflow-hidden border border-border transition-all duration-300 hover:-translate-y-1 rounded-none flex flex-col h-full"
              style={{ opacity: prefersReducedMotion() ? 1 : 0 }}
            >
              {/* Mock Browser Frame */}
              <div className="relative group/screenshot p-1 pb-0 bg-muted">
                {/* Browser Chrome Header */}
                <div className="mock-browser">
                  <div className="mock-browser-dot" />
                  <div className="mock-browser-dot" />
                  <div className="mock-browser-dot" />
                </div>
                
                {/* Screenshot Container */}
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block overflow-hidden aspect-video relative portfolio-screenshot-container bg-muted"
                >
                  <img
                    src={project.image}
                    alt={`Screenshot of ${project.title}`}
                    className="w-full h-full portfolio-screenshot"
                    loading="lazy"
                  />
                  {/* Diagonal Glass Sweep */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/screenshot:animate-shine pointer-events-none z-10" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover/screenshot:opacity-100 transition-opacity duration-300 z-10" />
                  <span className="absolute top-3 right-3 bg-foreground text-background text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-none border border-border z-20">
                    {project.category}
                  </span>
                </a>
              </div>

              {/* Content */}
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-sans uppercase font-bold text-sm tracking-wider mb-2 text-foreground group-hover:text-foreground">
                  {project.title}
                </h3>
                <p className="text-muted-foreground text-xs leading-relaxed mb-4">
                  {project.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-none bg-muted border border-border text-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-auto pt-2">
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1 text-xs font-bold uppercase tracking-wider rounded-none bg-foreground text-background border border-foreground hover:bg-background hover:text-foreground transition-all duration-200"
                    asChild
                  >
                    <a href={project.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                      Visit Site
                    </a>
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
